import React, { useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  IconButton,
} from "@mui/material";
import WindowOutlinedIcon from "@mui/icons-material/WindowOutlined";
import CalendarViewMonthOutlinedIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import DropDownList from "./DropDownList";
import ApiClient from "../../apis/Api-client";
import { format } from "date-fns";
import { END_POINT } from "../../utils/End-points";
import CloseIcon from "@mui/icons-material/Close";

const LiveViewPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [tabFilters, setTabFilters] = useState(["Default"]);
  const [cameraFeeds, setCameraFeeds] = useState([]);

  const { api } = ApiClient();

  useEffect(() => {
    const fetchTabFiltersFromFloors = async () => {
      try {
        const response = await api.get(
          `${END_POINT.FLOOR.GET_ALL_FLOORS}/${format(
            new Date(),
            "yyyy-MM-dd"
          )}`,
          null,
          "Error Fetching floors"
        );
        const floors = response?.data?.data || [];
        const floorNames = Array.from(
          new Set(floors.map((floor) => floor.floor_name).filter(Boolean))
        );
        const updatedTabs = ["Default", ...floorNames];
        setTabFilters(updatedTabs);

        // Now generate camera feeds dynamically based on new tab filters
        const categories = updatedTabs.slice(1);
        const feeds = Array.from({ length: 24 }, (_, index) => ({
          id: index + 1,
          title: `Camera ${String(index + 1).padStart(2, "0")}`,
          timestamp: new Date().toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
          isLive: Math.random() > 0.5,
          imgUrl: `https://placehold.co/300x200?text=Camera%20${index + 1}`,
          category: categories[index % categories.length],
        }));
        setCameraFeeds(feeds);
      } catch (error) {
        console.error("Failed to fetch tab filters:", error?.message);
      }
    };

    fetchTabFiltersFromFloors();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedCamera(null);
  };

  const filteredCameras = cameraFeeds.filter((camera) =>
    activeTab === 0 ? true : camera.category === tabFilters[activeTab]
  );
  return (
    <Box
      sx={{
        fontFamily: "Roboto, sans-serif",
        position: "relative",
      }}
    >
      <Box>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          {tabFilters.map((label, index) => (
            <Tab
              key={label}
              label={label}
              sx={{
                textTransform: "none",
                color: activeTab === index ? "#CE8500" : "#545454",
                backgroundColor: activeTab === index ? "#FFF6E8" : "#F8F8F8",
                fontWeight: activeTab === index ? "bold" : "normal",
                borderRadius: 2,
                border:
                  activeTab === index
                    ? "1px solid #CE8500 "
                    : " 1px solid #D8D8D8",
                mx: 0.5,
                my: 1,
                height: "2.083vw",
                minHeight: "2.083vw",
              }}
            />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ backgroundColor: "#f5f5f5" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",

            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, p: 1 }}>
            {activeTab === 0 ? "All Cameras" : tabFilters[activeTab]}
          </Typography>

          <DropDownList
            viewMode={viewMode}
            setViewMode={setViewMode}
            selectedCamera={selectedCamera}
          />
        </Box>

        {/* Camera Grid */}
        <Grid container spacing={2} sx={{ px: 2 }}>
          {selectedCamera ? (
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: 3,
                  position: "relative",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <IconButton
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
                  }}
                  onClick={() => setSelectedCamera(null)}
                >
                  <CloseIcon />
                </IconButton>
                {selectedCamera.isLive && (
                  <Chip
                    label="LIVE"
                    color="error"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: 8,
                      fontWeight: 600,
                      zIndex: 1,
                    }}
                  />
                )}
                <CardMedia
                  component="img"
                  height="400"
                  image={selectedCamera.imgUrl}
                  alt={selectedCamera.title}
                  sx={{ objectFit: "cover" }}
                />
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {selectedCamera.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedCamera.timestamp}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {selectedCamera.category}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            filteredCameras.map((camera) => (
              <Grid
                item
                key={camera.id}
                xs={12}
                sm={viewMode === "list" ? 4 : 6}
                md={viewMode === "list" ? 4 : 4}
                lg={viewMode === "list" ? 6 : 3}
              >
                <Card
                  sx={{
                    borderRadius: 2,
                    boxShadow: 3,
                    position: "relative",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    "&:hover": { boxShadow: 6 },
                  }}
                  onClick={() => setSelectedCamera(camera)}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={camera.imgUrl}
                    alt={camera.title}
                    sx={{ objectFit: "cover" }}
                  />
                  {camera.isLive && (
                    <Chip
                      label="LIVE"
                      color="error"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {camera.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {camera.timestamp}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {camera.category}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Empty State */}
        {!selectedCamera && filteredCameras.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No cameras found in this category
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LiveViewPage;
